/**
 * Per-property-type search-filter matching.
 *
 * Decides which filter fields the home-hero Filters panel (and, by extension, the
 * results-page sidebar) should show for a given property type, so a search never
 * asks for a field that doesn't apply to the type — e.g. picking أرض (land) hides
 * bedrooms/bathrooms and shows plot size instead.
 *
 * This is driven by `getVisibleFieldGroups()` — the SAME single-source-of-truth the
 * unified listing form uses (config/propertyTypeConfig.ts) — with a few search-context
 * refinements (price is always filterable; industrial types drop bathrooms; hotel uses
 * "Rooms" wording; land/farm relabel area to "Plot size"/"Farm area"). Keeping search
 * gated by the same logic as the listing form means a type can never offer a field the
 * listing form wouldn't have collected.
 *
 * A field the UI hides must also never be SENT: `pruneHiddenFilters()` blanks hidden
 * ranges and amenity toggles before a URL or API request is built.
 */
import { getVisibleFieldGroups, getPropertyCategory, getAllowedAmenities } from './propertyTypeConfig';
import { getSearchTypeOptions } from './propertyTypeCatalog';
import type { SearchListingType } from './searchPresets';

export interface SearchFilterFields {
  bedrooms: boolean;
  bathrooms: boolean;
  area: boolean;
  price: boolean;
  /** "Rooms" for hotels, "Bedrooms" otherwise. */
  bedroomsLabelAr: string;
  bedroomsLabelEn: string;
  /** "Plot size" for land, "Farm area" for farms, "Area" otherwise. */
  areaLabelAr: string;
  areaLabelEn: string;
}

/** The سكني / تجاري toggle. '' (or 'all') = every category — /search only. */
export type SearchCategory = '' | 'all' | 'residential' | 'commercial';

/**
 * Which range fields show while NO property type is chosen — the owner's decision
 * (overhaul plan, "Defaults I chose"; change it here, in one place):
 *   سكني → bedrooms + bathrooms (+ price + area)
 *   تجاري → price + area only
 *   الكل (/search only) → treated like سكني
 * Price and area always show. Once a type IS chosen, that type's own rules apply.
 */
export const NO_TYPE_FIELD_RULE: Readonly<
  Record<'residential' | 'commercial' | 'all', { readonly bedrooms: boolean; readonly bathrooms: boolean }>
> = {
  residential: { bedrooms: true, bathrooms: true },
  commercial: { bedrooms: false, bathrooms: false },
  all: { bedrooms: true, bathrooms: true },
};

// Types that speak of "Rooms" rather than "Bedrooms".
const ROOMS_WORDING = new Set(['hotel']);
// Industrial / utility commercial types that should NOT expose a bathrooms filter,
// mirroring the listing-form overrides (warehouse/factory show area + parking only).
const NO_BATHROOM_COMMERCIAL = new Set(['warehouse', 'factory', 'workshop']);

const DEFAULT_LABELS = {
  bedroomsLabelAr: 'غرف النوم',
  bedroomsLabelEn: 'Bedrooms',
  areaLabelAr: 'المساحة',
  areaLabelEn: 'Area',
};

function categoryKey(category: SearchCategory | null | undefined): 'residential' | 'commercial' | 'all' {
  return category === 'residential' || category === 'commercial' ? category : 'all';
}

/**
 * @param propertyType lowercase option id (e.g. 'villa', 'land', 'commercial_land'),
 *   the UPPERCASE enum, or ''.
 * @param category the active macro-toggle ('residential' | 'commercial' | '' / 'all') —
 *   only consulted when no specific type is chosen (see NO_TYPE_FIELD_RULE).
 */
export function getSearchFilterFields(
  propertyType: string,
  category: SearchCategory = '',
): SearchFilterFields {
  const id = (propertyType || '').toLowerCase();

  // No specific type — the owner's per-category default (NO_TYPE_FIELD_RULE).
  if (!id) {
    const rule = NO_TYPE_FIELD_RULE[categoryKey(category)];
    return {
      bedrooms: rule.bedrooms,
      bathrooms: rule.bathrooms,
      area: true,
      price: true,
      ...DEFAULT_LABELS,
    };
  }

  const groups = getVisibleFieldGroups(id);
  const cat = getPropertyCategory(id.toUpperCase());
  const isLandLike = cat === 'land';

  let bathrooms = groups.bathrooms;
  if (NO_BATHROOM_COMMERCIAL.has(id)) bathrooms = false;

  const rooms = ROOMS_WORDING.has(id);
  const isFarm = id === 'farm';

  return {
    bedrooms: groups.bedrooms,
    bathrooms,
    area: true, // area / plot size is meaningful for every type in a search
    price: true, // every listing has a price; always filterable in search
    bedroomsLabelAr: rooms ? 'غرف' : DEFAULT_LABELS.bedroomsLabelAr,
    bedroomsLabelEn: rooms ? 'Rooms' : DEFAULT_LABELS.bedroomsLabelEn,
    areaLabelAr: isFarm ? 'مساحة المزرعة' : isLandLike ? 'مساحة الأرض' : DEFAULT_LABELS.areaLabelAr,
    areaLabelEn: isFarm ? 'Farm area' : isLandLike ? 'Plot size' : DEFAULT_LABELS.areaLabelEn,
  };
}

// --- Range columns -----------------------------------------------------------

export type SearchRangeKey = 'bedrooms' | 'bathrooms' | 'price' | 'area';

/**
 * Range columns in DOM order. In RTL this renders right-to-left as the owner
 * listed them: غرف النوم، دورات المياه، السعر، المساحة.
 */
export const SEARCH_RANGE_ORDER: readonly SearchRangeKey[] = ['bedrooms', 'bathrooms', 'price', 'area'];

/** The range columns to show for a type / category, in SEARCH_RANGE_ORDER. */
export function getVisibleRangeKeys(propertyType: string, category: SearchCategory = ''): SearchRangeKey[] {
  const fields = getSearchFilterFields(propertyType, category);
  return SEARCH_RANGE_ORDER.filter((key) => fields[key]);
}

export interface SearchRangeBounds {
  min: string;
  max: string;
}

/** A range picker's value: digit strings, '' = no bound. */
export type SearchRanges = Record<SearchRangeKey, SearchRangeBounds>;

/** The flat filter / URL keys behind each range (FilterState, hero state, /search params). */
export const RANGE_FILTER_KEYS = {
  bedrooms: { min: 'bedrooms', max: 'bedroomsMax' },
  bathrooms: { min: 'bathrooms', max: 'bathroomsMax' },
  price: { min: 'minPrice', max: 'maxPrice' },
  area: { min: 'minArea', max: 'maxArea' },
} as const;

export interface FlatRangeFilters {
  bedrooms: string;
  bedroomsMax: string;
  bathrooms: string;
  bathroomsMax: string;
  minPrice: string;
  maxPrice: string;
  minArea: string;
  maxArea: string;
}

/** A fresh, all-empty range value. */
export function createEmptySearchRanges(): SearchRanges {
  return {
    bedrooms: { min: '', max: '' },
    bathrooms: { min: '', max: '' },
    price: { min: '', max: '' },
    area: { min: '', max: '' },
  };
}

/** Flat filter keys → the nested value a range picker edits. Missing keys read as ''. */
export function rangesFromFilters(filters: Partial<FlatRangeFilters>): SearchRanges {
  const ranges = createEmptySearchRanges();
  for (const key of SEARCH_RANGE_ORDER) {
    ranges[key] = {
      min: filters[RANGE_FILTER_KEYS[key].min] ?? '',
      max: filters[RANGE_FILTER_KEYS[key].max] ?? '',
    };
  }
  return ranges;
}

/** The nested range value → flat filter keys (to spread into FilterState / hero state). */
export function rangesToFilters(ranges: SearchRanges): FlatRangeFilters {
  return {
    bedrooms: ranges.bedrooms.min,
    bedroomsMax: ranges.bedrooms.max,
    bathrooms: ranges.bathrooms.min,
    bathroomsMax: ranges.bathrooms.max,
    minPrice: ranges.price.min,
    maxPrice: ranges.price.max,
    minArea: ranges.area.min,
    maxArea: ranges.area.max,
  };
}

/** True when both bounds are set and «من» is greater than «إلى» (the picker shows an error). */
export function isInvertedRange({ min, max }: SearchRangeBounds): boolean {
  if (!min || !max) return false;
  const lo = Number(min);
  const hi = Number(max);
  return Number.isFinite(lo) && Number.isFinite(hi) && lo > hi;
}

// --- Amenity toggles ---------------------------------------------------------

/** The amenity toggles the search UI offers — the FilterState boolean keys. */
export type SearchAmenityKey =
  | 'parking'
  | 'petFriendly'
  | 'furnished'
  | 'swimmingPool'
  | 'gym'
  | 'garden'
  | 'elevator'
  | 'security';

/** Every search amenity toggle, in display order. */
export const SEARCH_AMENITY_KEYS: readonly SearchAmenityKey[] = [
  'parking',
  'petFriendly',
  'furnished',
  'swimmingPool',
  'gym',
  'garden',
  'elevator',
  'security',
];

// Toggles backed by an amenity id in the listing form's allow-lists
// (getAllowedAmenities). parking / furnished come from the field groups instead.
const AMENITY_ID_BY_KEY: ReadonlyArray<[SearchAmenityKey, string]> = [
  ['petFriendly', 'pet_friendly'],
  ['swimmingPool', 'pool'],
  ['gym', 'gym'],
  ['garden', 'garden'],
  ['elevator', 'elevator'],
  ['security', 'security'],
];

function amenitiesForType(id: string, listingType: SearchListingType): Set<SearchAmenityKey> {
  const groups = getVisibleFieldGroups(id);
  // getAllowedAmenities already drops the rental-only pet policy for 'sale'.
  const allowed = new Set(getAllowedAmenities(id, listingType === 'buy' ? 'sale' : listingType));
  const keys = new Set<SearchAmenityKey>();
  if (groups.parking) keys.add('parking');
  if (groups.furnishing) keys.add('furnished');
  for (const [key, amenityId] of AMENITY_ID_BY_KEY) {
    if (allowed.has(amenityId)) keys.add(key);
  }
  if (listingType === 'buy') keys.delete('petFriendly');
  return keys;
}

/**
 * The amenity toggles that make sense for a search, in SEARCH_AMENITY_KEYS order —
 * the same allow-lists the listing form uses, so a land search never offers a pool.
 * With no type chosen: the union over the category's non-land types.
 * Pet-friendly is a rental policy, so it never shows for 'buy'.
 */
export function getSearchAmenities(
  propertyType: string,
  category: SearchCategory = '',
  listingType: SearchListingType = 'all',
): SearchAmenityKey[] {
  const id = (propertyType || '').toLowerCase();
  const key = categoryKey(category);
  const typeIds = id
    ? [id]
    : getSearchTypeOptions(key === 'all' ? '' : key)
        .filter((option) => option.category !== 'land')
        .map((option) => option.id);
  const union = new Set<SearchAmenityKey>();
  for (const typeId of typeIds) {
    for (const amenity of amenitiesForType(typeId, listingType)) union.add(amenity);
  }
  return SEARCH_AMENITY_KEYS.filter((amenity) => union.has(amenity));
}

// --- Pruning -----------------------------------------------------------------

/**
 * The fields pruneHiddenFilters reads. The /search FilterState and the hero's
 * state both fit; keys a caller doesn't have are simply skipped.
 */
export interface PrunableSearchFilters {
  propertyType?: string;
  category?: SearchCategory;
  listingType?: SearchListingType;
  bedrooms?: string;
  bedroomsMax?: string;
  bathrooms?: string;
  bathroomsMax?: string;
  minArea?: string;
  maxArea?: string;
  parking?: boolean;
  petFriendly?: boolean;
  furnished?: boolean;
  swimmingPool?: boolean;
  gym?: boolean;
  garden?: boolean;
  elevator?: boolean;
  security?: boolean;
}

type PrunableRangeKey = 'bedrooms' | 'bedroomsMax' | 'bathrooms' | 'bathroomsMax' | 'minArea' | 'maxArea';

const PRUNABLE_RANGES: ReadonlyArray<['bedrooms' | 'bathrooms' | 'area', ReadonlyArray<PrunableRangeKey>]> = [
  ['bedrooms', ['bedrooms', 'bedroomsMax']],
  ['bathrooms', ['bathrooms', 'bathroomsMax']],
  ['area', ['minArea', 'maxArea']],
];

/**
 * Blanks every value the UI hides for the current type / category / listing
 * scope — bedroom, bathroom and area ranges plus disallowed amenity toggles — so a
 * hidden value can never reach a URL or the search API (e.g. villa with 3 bedrooms,
 * then switching to أرض, drops `bedrooms`). Keys the input doesn't have are never
 * added. Returns the SAME object when nothing needed pruning.
 */
export function pruneHiddenFilters<T extends PrunableSearchFilters>(filters: T): T {
  const propertyType = filters.propertyType ?? '';
  const category = filters.category ?? '';
  const fields = getSearchFilterFields(propertyType, category);
  const amenities = new Set(getSearchAmenities(propertyType, category, filters.listingType ?? 'all'));

  const patch: Partial<Record<PrunableRangeKey | SearchAmenityKey, '' | false>> = {};
  for (const [field, keys] of PRUNABLE_RANGES) {
    if (fields[field]) continue;
    for (const key of keys) {
      const value = filters[key];
      if (typeof value === 'string' && value !== '') patch[key] = '';
    }
  }
  for (const amenity of SEARCH_AMENITY_KEYS) {
    if (filters[amenity] === true && !amenities.has(amenity)) patch[amenity] = false;
  }

  return Object.keys(patch).length > 0 ? { ...filters, ...patch } : filters;
}
